import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import Dashboard from "../../dashboard/Dashboard";
import { useNavigate, useParams } from "react-router-dom";
import { ProcessTable } from "./ProcessTable";
import { useDispatch, useSelector } from "react-redux";
import { editJob, getSingleJob } from "../../../actions/job";
import { ArrowBack } from "../../../common/BackArrow";
import { formattedEditDate } from "../../../common/formattedDate";
import Loader from "../../loader/Loader";
import MyModal from "../../../common/Modal";
import { idleCode } from "../../../common/Data";
import {
  formatDecimalHoursToHoursMinutes,
  formatDecimalMinutesToMinutesSeconds,
} from "../../../utils/timeHelper";

function UpdateJob() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { loading } = useSelector((state) => state.job);
  const navigate = useNavigate();
  const [containers, setContainers] = useState([
    {
      processName: "",
      processTableData: [],
    },
  ]);
  const [formData, setFormData] = useState({
    soWo: "",
    prodOrderNo: "",
    woDate: new Date().toISOString().split("T")[0],
    jobName: "",
    poNo: "",
    estimatedtotalCT: "",
    actualtotalCT: "",
    dragNo: "",
    processTable: [...containers],
  });
  const [errors, setErrors] = useState({
    soWo: false,
    prodOrderNo: false,
    jobName: false,
    poNo: false,
    estimatedtotalCT: false,
    dragNo: false,
  });

  const [processTableErrors, setProcessTableErrors] = useState(
    formData?.processTable?.map(() => ({
      process: false,
      description: false,
      machineName: false,
      toolingUsed: false,
      dc: false,
      length: false,
      width: false,
      feed: false,
      estimatedCT: false,
    })) || []
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [deleteModalState, setDeleteModalState] = useState({
    open: false,
    rowIndex: null,
    idleIndex: null,
    containerIndex: null,
  });

  // Format functions for display
  const formatCTForDisplay = (ctValue) => {
    if (!ctValue || isNaN(ctValue)) return "00:00";
    return formatDecimalMinutesToMinutesSeconds(ctValue);
  };

  const formatEstimatedHrsForDisplay = (hoursValue) => {
    if (!hoursValue || isNaN(hoursValue)) return "00:00:00";
    return formatDecimalHoursToHoursMinutes(hoursValue);
  };

  const formatTotalCTForDisplay = (totalCTValue) => {
    if (!totalCTValue || isNaN(totalCTValue)) return "00:00";
    return formatDecimalMinutesToMinutesSeconds(totalCTValue);
  };

  const handleDeleteClick = (rowIndex, idleIndex, containerIndex) => {
    setDeleteModalState({
      open: true,
      rowIndex,
      idleIndex,
      containerIndex,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ open: false, rowIndex: null, idleIndex: null });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dispatch(getSingleJob(id));
        setFormData(response.payload);
        setContainers(response.payload.processTable);
      } catch (error) {
        console.error("Error fetching job data:", error);
      }
    };

    fetchData();
  }, [dispatch, id]);

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      processTable: containers,
    }));
  }, [containers]);

  const handleOpenModal = (message) => {
    setModalMessage(message);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalMessage("");
  };

  const prepareFormData = (currentFormData, containers) => {
    const totalEstimatedCT = containers?.reduce((total, container) => {
      const containerCT = container?.processTableData.reduce(
        (subTotal, data) => {
          return !isNaN(data?.actualCT) ? subTotal + data?.actualCT : subTotal;
        },
        0
      );
      return total + containerCT;
    }, 0);

    return {
      ...currentFormData,
      processTable: containers,
      actualtotalCT: totalEstimatedCT,
    };
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const dataToSubmit = prepareFormData(formData, containers);

    try {
      const editFormData = {
        formData: dataToSubmit,
        id,
      };

      const result = await dispatch(editJob(editFormData));
      if (result?.payload?.success === false) {
        handleOpenModal("Update failed. Please try again.");
      } else {
        handleOpenModal("Job updated successfully!");
      }
    } catch (error) {
      console.error("Error updating job:", error);
      handleOpenModal("An error occurred. Please try again.");
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: false,
      }));
    }
  };

  const calculateTotalCT = () => {
    const totalEstimatedCT = containers?.reduce((total, container) => {
      const containerEstimatedCT = container.processTableData.reduce(
        (containerTotal, data) => {
          if (!isNaN(data?.actualCT)) {
            return containerTotal + data?.actualCT;
          }
          return containerTotal;
        },
        0
      );
      return total + containerEstimatedCT;
    }, 0);

    setFormData((prevData) => ({
      ...prevData,
      actualtotalCT: totalEstimatedCT,
    }));
  };

  const calculateTimeDifference = (startDate, startTime, endDate, endTime) => {
    if (!startDate || !startTime || !endDate || !endTime) return null;

    try {
      const startDateOnly = startDate.split("T")[0];
      const endDateOnly = endDate.split("T")[0];

      const start = new Date(`${startDateOnly}T${startTime}`);
      const end = new Date(`${endDateOnly}T${endTime}`);

      const diffMinutes = Math.round((end - start) / (1000 * 60));
      return diffMinutes >= 0 ? diffMinutes : 0;
    } catch (error) {
      console.error("Error calculating time difference:", error);
      return 0;
    }
  };

  const calculateCT = (rowData) => {
    try {
      if (
        !rowData.startDate ||
        !rowData.startTime ||
        !rowData.endDate ||
        !rowData.endTime
      ) {
        return rowData.actualCT || 0;
      }

      const parseDateTime = (dateStr, timeStr) => {
        const dateOnly = dateStr.split("T")[0];
        return new Date(`${dateOnly}T${timeStr}:00`);
      };

      const startMain = parseDateTime(rowData.startDate, rowData.startTime);
      const endMain = parseDateTime(rowData.endDate, rowData.endTime);

      let totalMinutes = (endMain - startMain) / (1000 * 60);

      if (rowData.idleCode?.length > 0) {
        let totalIdleMinutes = 0;

        rowData.idleCode.forEach((idle) => {
          if (
            idle.startDate &&
            idle.startTime &&
            idle.endDate &&
            idle.endTime
          ) {
            const startIdle = parseDateTime(idle.startDate, idle.startTime);
            const endIdle = parseDateTime(idle.endDate, idle.endTime);
            const idleMinutes = (endIdle - startIdle) / (1000 * 60);
            totalIdleMinutes += idleMinutes;
          }
        });

        totalMinutes += totalIdleMinutes;
      }

      const finalCT = Math.max(0, Math.round(totalMinutes));
      return finalCT;
    } catch (error) {
      console.error("Error calculating CT:", error);
      return rowData.actualCT || 0;
    }
  };

  const formatTotalEstimatedCTWithBreakdown = (containersData) => {
    let processTime = 0;
    let settingTime = 0;

    containersData.forEach((container) => {
      settingTime += parseFloat(container.setting) || 0;
      container.processTableData.forEach((row) => {
        processTime += parseFloat(row.estimatedCT) || 0;
      });
    });

    const total = processTime + settingTime;

    return {
      total: formatDecimalMinutesToMinutesSeconds(total),
      processTime: formatDecimalMinutesToMinutesSeconds(processTime),
      settingTime: formatDecimalMinutesToMinutesSeconds(settingTime),
      rawTotal: total,
    };
  };

  // Usage in your component:
  const totalCTBreakdown = formatTotalEstimatedCTWithBreakdown(containers);

  const handleTextFieldChange = (
    event,
    rowIndex,
    fieldName,
    containerIndex,
    processName
  ) => {
    const value = event.target.value;
    const isBlurEvent = event.type === "blur";

    setContainers((prevContainers) => {
      const updatedContainers = prevContainers.map((container, idx) => {
        if (idx === containerIndex) {
          const updatedProcessTableData = container.processTableData.map(
            (data, index) => {
              let updatedData = { ...data };
              if (index === rowIndex) {
                updatedData = { ...updatedData, [fieldName]: value };

                // Process-specific calculations
                if (processName === "Milling") {
                  if (
                    [
                      "toolingSize",
                      "dia",
                      "width",
                      "length",
                      "feed",
                      "nop",
                      "fpp",
                      "mr",
                      "dc",
                    ].includes(fieldName)
                  ) {
                    updatedData.dia = updatedData.toolingSize * 0.9;
                    updatedData.nop = parseFloat(
                      (updatedData.width / updatedData.dia).toFixed(2)
                    );
                    updatedData.fpp = parseFloat(
                      (updatedData.length / updatedData.feed).toFixed(2)
                    );
                    updatedData.estimatedCT =
                      updatedData.nop *
                      updatedData.fpp *
                      (updatedData.mr / updatedData.dc) *
                      1.25;

                    // Format estimatedHrs for display
                    updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                  } else if (
                    [
                      "startTime",
                      "endTime",
                      "startTime1",
                      "endTime1",
                      "idleCode",
                      "estimatedCT",
                    ].includes(fieldName)
                  ) {
                    const diff = calculateCT(updatedData);
                    updatedData.actualCT = diff;
                    if (
                      updatedData.estimatedCT !== undefined &&
                      !isNaN(updatedData.estimatedCT)
                    ) {
                      updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                    }
                  }
                } else if (processName === "Boring") {
                  updatedData[fieldName] = value;
                  if (
                    [
                      "mr",
                      "dc",
                      "length",
                      "feed",
                      "rpm",
                      "nop",
                      "fpp",
                    ].includes(fieldName)
                  ) {
                    updatedData.nop = parseFloat(
                      (updatedData.mr / updatedData.dc).toFixed(2)
                    );
                    updatedData.fpp = parseFloat(
                      (
                        updatedData.length /
                        (updatedData.rpm * updatedData.feed)
                      ).toFixed(2)
                    );
                    updatedData.estimatedCT = updatedData.nop * updatedData.fpp;

                    updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                  } else if (
                    [
                      "startTime",
                      "endTime",
                      "startTime1",
                      "endTime1",
                      "idleCode",
                    ].includes(fieldName)
                  ) {
                    const diff = calculateCT(updatedData);
                    updatedData.actualCT = diff;
                    if (
                      updatedData.estimatedCT !== undefined &&
                      !isNaN(updatedData.estimatedCT)
                    ) {
                      updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                    }
                  }
                } else if (processName === "Drilling") {
                  updatedData[fieldName] = value;
                  if (["noh", "legnth", "feed"].includes(fieldName)) {
                    updatedData.estimatedCT =
                      ((updatedData.length * 1.05) / updatedData.feed) *
                      updatedData.noh;

                    updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                  } else if (
                    [
                      "startTime",
                      "endTime",
                      "startTime1",
                      "endTime1",
                      "idleCode",
                    ].includes(fieldName)
                  ) {
                    const diff = calculateCT(updatedData);
                    updatedData.actualCT = diff;
                    if (
                      updatedData.estimatedCT !== undefined &&
                      !isNaN(updatedData.estimatedCT)
                    ) {
                      updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                    }
                  }
                } else if (processName === "Tapping") {
                  updatedData[fieldName] = value;
                  if (["noh", "legnth", "rpm"].includes(fieldName)) {
                    updatedData.estimatedCT = parseFloat(
                      (updatedData.length / (updatedData.rpm * 1.5)) *
                        updatedData.noh *
                        1.3
                    );
                    updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                  } else if (
                    [
                      "startTime",
                      "endTime",
                      "startTime1",
                      "endTime1",
                      "idleCode",
                      "estimatedCT",
                    ].includes(fieldName)
                  ) {
                    const diff = calculateCT(updatedData);
                    updatedData.actualCT = diff;
                  }
                }
                if (
                  updatedData.estimatedCT !== undefined &&
                  !isNaN(updatedData.estimatedCT)
                ) {
                  updatedData.estimatedHrs = updatedData.estimatedCT / 60;
                }
                return updatedData;
              }
              return data;
            }
          );
          return { ...container, processTableData: updatedProcessTableData };
        }
        return container;
      });

      if (isBlurEvent) {
        handleSubmit();
      }

      return updatedContainers;
    });

    if (fieldName === "idleCode") {
      calculateTotalCT();
    }
  };

  const handleDropdownChange = (e, containerIndex) => {
    const updatedContainers = [...containers];
    updatedContainers[containerIndex].processName = e.target.value;
    setContainers(updatedContainers);
  };

  const handleDeleteConfirm = async () => {
    const { rowIndex, idleIndex, containerIndex } = deleteModalState;

    setContainers((prevContainers) => {
      const updatedContainers = prevContainers.map((container, cIdx) => {
        if (cIdx === containerIndex) {
          const updatedProcessTableData = container.processTableData.map(
            (row, rIdx) => {
              if (rIdx === rowIndex) {
                const newIdleCodes = [
                  ...(row.idleCode || []).slice(0, idleIndex),
                  ...(row.idleCode || []).slice(idleIndex + 1),
                ];

                return {
                  ...row,
                  idleCode: newIdleCodes,
                  actualCT: calculateCT({
                    ...row,
                    idleCode: newIdleCodes,
                    startDate: row.startDate,
                    startTime: row.startTime,
                    endDate: row.endDate,
                    endTime: row.endTime,
                  }),
                };
              }
              return row;
            }
          );

          return {
            ...container,
            processTableData: updatedProcessTableData,
          };
        }
        return container;
      });

      setTimeout(() => {
        const dataToSubmit = prepareFormData(formData, updatedContainers);
        const editFormData = {
          formData: dataToSubmit,
          id,
        };
        dispatch(editJob(editFormData));
      }, 300);

      return updatedContainers;
    });

    setDeleteModalState({ open: false, rowIndex: null, idleIndex: null });
  };

  const addIdleCode = (rowIndex, containerIndex, processName) => {
    setContainers((prevContainers) => {
      const updatedContainers = prevContainers.map((container, cIdx) => {
        if (cIdx === containerIndex) {
          const updatedProcessTableData = container.processTableData.map(
            (row, rIdx) => {
              if (rIdx === rowIndex) {
                const newIdleCode = {
                  code: "",
                  startDate: "",
                  startTime: "",
                  endDate: "",
                  endTime: "",
                  timeDifference: 0,
                };

                return {
                  ...row,
                  idleCode: [...(row.idleCode || []), newIdleCode],
                };
              }
              return row;
            }
          );

          return {
            ...container,
            processTableData: updatedProcessTableData,
          };
        }
        return container;
      });

      return updatedContainers;
    });
  };

  const handleIdleTimeChange = (
    e,
    rowIndex,
    idleIndex,
    field,
    containerIndex,
    processName
  ) => {
    const newValue = e.target.value;

    setContainers((prevContainers) => {
      const updatedContainers = prevContainers.map((container, cIdx) => {
        if (cIdx === containerIndex) {
          const updatedProcessTableData = container.processTableData.map(
            (row, rIdx) => {
              if (rIdx === rowIndex) {
                const updatedIdleCodes = [
                  ...(row.idleCode || []).map((code) => ({ ...code })),
                ];

                if (!updatedIdleCodes[idleIndex]) {
                  updatedIdleCodes[idleIndex] = {
                    code: "",
                    startDate: "",
                    startTime: "",
                    endDate: "",
                    endTime: "",
                    timeDifference: 0,
                  };
                }

                updatedIdleCodes[idleIndex][field] = newValue;

                const idle = updatedIdleCodes[idleIndex];
                if (
                  idle.startDate &&
                  idle.startTime &&
                  idle.endDate &&
                  idle.endTime
                ) {
                  const diff = calculateTimeDifference(
                    idle.startDate,
                    idle.startTime,
                    idle.endDate,
                    idle.endTime
                  );
                  updatedIdleCodes[idleIndex].timeDifference =
                    diff !== null ? diff : 0;
                } else {
                  updatedIdleCodes[idleIndex].timeDifference = 0;
                }

                const updatedRow = {
                  ...row,
                  idleCode: updatedIdleCodes,
                  actualCT: calculateCT({
                    ...row,
                    idleCode: updatedIdleCodes,
                    startDate: row.startDate,
                    startTime: row.startTime,
                    endDate: row.endDate,
                    endTime: row.endTime,
                  }),
                };

                return updatedRow;
              }
              return row;
            }
          );

          return { ...container, processTableData: updatedProcessTableData };
        }
        return container;
      });

      if (e.type === "blur") {
        setTimeout(() => {
          const dataToSubmit = prepareFormData(formData, updatedContainers);
          const editFormData = {
            formData: dataToSubmit,
            id,
          };
          dispatch(editJob(editFormData));
        }, 300);
      }

      return updatedContainers;
    });
  };

  return (
    <Dashboard>
      <>
        <Box display={"flex"}>
          <ArrowBack />
          <h2>Update Job</h2>
        </Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="spanning table">
            <TableBody>
              <TableRow>
                <TableCell align="center">SO/Wo No</TableCell>
                <TableCell align="center">
                  <TextField
                    label="So/Wo No"
                    id="outlined-size-small"
                    size="small"
                    variant="outlined"
                    name="soWo"
                    disabled
                    value={formData?.soWo}
                    onChange={handleChange}
                    error={errors.soWo}
                    helperText={errors.soWo ? "This field is required" : ""}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </TableCell>
                <TableCell align="center">Prod.Order No</TableCell>
                <TableCell align="center">
                  <TextField
                    label="Prod.Order No"
                    id="outlined-size-small"
                    size="small"
                    name="prodOrderNo"
                    value={formData?.prodOrderNo}
                    onChange={handleChange}
                    disabled
                    error={errors.prodOrderNo}
                    helperText={
                      errors.prodOrderNo ? "This field is required" : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </TableCell>
                <TableCell align="center">PO No</TableCell>
                <TableCell align="center">
                  <TextField
                    label="PO No"
                    id="outlined-size-small"
                    size="small"
                    name="poNo"
                    value={formData?.poNo}
                    onChange={handleChange}
                    error={errors.poNo}
                    disabled
                    helperText={errors.poNo ? "This field is required" : ""}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </TableCell>
                {/* <TableCell align="center">WO Date</TableCell>
                <TableCell align="center">
                  <TextField
                    id="date"
                    label="WO Date"
                    type="date"
                    size="small"
                    variant="outlined"
                    disabled
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="woDate"
                    value={formattedEditDate(formData?.woDate)}
                    onChange={handleChange}
                  />
                </TableCell> */}
              </TableRow>
              <TableRow>
                <TableCell align="center">Job Name</TableCell>
                <TableCell align="center">
                  <TextField
                    label="Job Name"
                    id="outlined-size-small"
                    size="small"
                    name="jobName"
                    value={formData?.jobName}
                    onChange={handleChange}
                    error={errors.jobName}
                    disabled
                    helperText={errors.jobName ? "This field is required" : ""}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </TableCell>
                <TableCell align="center">Drawing No</TableCell>
                <TableCell align="center">
                  <TextField
                    label="Drag No"
                    id="outlined-size-small"
                    size="small"
                    name="dragNo"
                    value={formData?.dragNo}
                    onChange={handleChange}
                    error={errors.dragNo}
                    helperText={errors.dragNo ? "This field is required" : ""}
                    disabled
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </TableCell>
                <TableCell align="center">Estimated CT</TableCell>
                <TableCell align="center">
                  <TextField
                    label="Estimated CT"
                    id="outlined-size-small"
                    size="small"
                    name="estimatedtotalCT"
                    value={formatTotalCTForDisplay(formData?.estimatedtotalCT)}
                    onChange={handleChange}
                    error={errors.estimatedtotalCT}
                    disabled
                    helperText={
                      errors.estimatedtotalCT ? "This field is required" : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {/* Optional: Show breakdown tooltip */}
                  <Tooltip
                    title={`Process Time: ${totalCTBreakdown.processTime} | Setting Time: ${totalCTBreakdown.settingTime}`}
                    arrow
                  >
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 0.5, color: "text.secondary" }}
                    >
                      (Includes setting time)
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {containers?.map((container, containerIndex) => (
          <TableContainer
            key={containerIndex}
            component={Paper}
            sx={{ marginTop: 3 }}
          >
            <TableRow sx={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <TableCell>{containerIndex + 1}</TableCell>
                <TableCell>
                  <FormControl>
                    <InputLabel
                      id="demo-multiple-name-label"
                      style={{ color: "#1D5393" }}
                    >
                      MainProcessName
                    </InputLabel>
                    <Select
                      value={container.processName}
                      label="processName"
                      className="input"
                      size="large"
                      disabled
                      onChange={(e) =>
                        handleDropdownChange(e, containerIndex, "processName")
                      }
                    >
                      <MenuItem value="Milling">MILLING</MenuItem>
                      <MenuItem value="Boring">BORING</MenuItem>
                      <MenuItem value="Drilling">DRILLING</MenuItem>
                      <MenuItem value="Tapping">TAPPING</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  Setting for All Process Admin have to add setting Time in
                  minutes
                </TableCell>
                <TableCell align="center">
                  <TextField
                    label="settingTime"
                    className="input"
                    size="small"
                    name={`setting-${containerIndex}`}
                    value={container.setting}
                    onChange={(e) =>
                      handleDropdownChange(e, containerIndex, "setting")
                    }
                    InputLabelProps={{
                      shrink: true, // This ensures the label shrinks when there's a value
                    }}
                    disabled
                  />
                </TableCell>
              </div>
            </TableRow>
            <ProcessTable
              key={containerIndex}
              data={container.processTableData}
              handleTextFieldChange={handleTextFieldChange}
              handleIdleTimeChange={handleIdleTimeChange}
              processTableErrors={processTableErrors}
              containerIndex={containerIndex}
              selectedProcessName={container.processName}
              calculateCT={calculateCT}
              handleDeleteConfirm={handleDeleteConfirm}
              addIdleCode={addIdleCode}
              deleteModalState={deleteModalState}
              handleDeleteCancel={handleDeleteCancel}
              handleDeleteClick={handleDeleteClick}
              calculateTimeDifference={calculateTimeDifference}
              formatCTForDisplay={formatCTForDisplay}
              formatEstimatedHrsForDisplay={formatEstimatedHrsForDisplay}
            />
          </TableContainer>
        ))}

        <Box display={"flex"} marginTop={5}>
          <div>
            <Button
              color="primary"
              sx={{
                backgroundColor: "#1d5393",
                color: "#fff",
                marginRight: "10px",
                "&:hover": {
                  backgroundColor: "#245BA1",
                },
                "&:active": {
                  backgroundColor: "#1d5393",
                },
              }}
              onClick={handleSubmit}
            >
              Update
            </Button>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <Button
              color="primary"
              sx={{
                backgroundColor: "#1d5393",
                color: "#fff",
                marginRight: 2,
                "&:hover": {
                  backgroundColor: "#245BA1",
                },
                "&:active": {
                  backgroundColor: "#1d5393",
                },
              }}
              onClick={calculateTotalCT}
            >
              Calculate Total CT
            </Button>
            <TextField
              label="Actual Total CT"
              id="outlined-size-small"
              size="small"
              name="actualtotalCT"
              disabled
              value={formatTotalCTForDisplay(formData?.actualtotalCT)}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
          <MyModal
            open={isModalOpen}
            onClose={handleCloseModal}
            title={modalMessage.includes("success") ? "Success" : "Error"}
            description={modalMessage}
            buttonText="OK"
            onButtonClick={handleCloseModal}
          />
        </Box>
      </>
    </Dashboard>
  );
}

export default UpdateJob;
